import { useEffect, useState } from 'react';
/* ============================================================================
   CONTACT — WhatsApp

   Two problems this solves:

   1. The pricing buttons were `mailto:` links. On a machine with no desktop
      mail client configured — which is most Windows/Chrome machines — a mailto
      does NOTHING when clicked. A principal who reaches the pricing section,
      decides to enquire, and clicks a dead button is the single most expensive
      failure on the page.

   2. Indian school principals live on WhatsApp. An email form is the wrong
      channel for this audience even when it works.

   ⚠ SCRAPING. A phone number in the HTML source gets harvested by bots
   continuously, and the spam never stops. So the number is assembled in JS from
   parts at click time and never appears as a complete string in the markup.
   This is not real security — a determined scraper runs JS — but it defeats the
   overwhelming majority, which are simple HTML crawlers.
   ============================================================================ */

// Split so the full number never appears as one literal in the served HTML.
const CC = '91';
const N1 = '76764';
const N2 = '18761';

export function waNumber(): string {
  return CC + N1 + N2;
}

/** Opens WhatsApp with a pre-filled message so the enquiry arrives with context. */
export function openWhatsApp(message: string) {
  const url = `https://wa.me/${waNumber()}?text=${encodeURIComponent(message)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** The message each entry point sends, so you know where the enquiry came from. */
export const WA_MESSAGES = {
  starter: 'Hello, I am interested in Eduvora for my school (Starter — up to 500 students). Could we talk?',
  growth: 'Hello, I am interested in Eduvora for my school (Growth — up to 3,000 students). Could we talk?',
  trust: 'Hello, I am interested in Eduvora for our trust (multi-campus). Could we talk?',
  demo: 'Hello, I would like a walkthrough of Eduvora.',
  support: 'Hello, I need help with Eduvora.',
};

/**
 * A floating WhatsApp button.
 *
 * Appears after the visitor has scrolled — a chat bubble covering the hero before anyone has read a
 * word is an irritation, not an invitation. It also sits above the footer links rather than on top of
 * them.
 */
export function WhatsAppFab() {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    // Reveal once the visitor has moved past roughly the hero. Before that the page is still making
    // its case, and a chat bubble over the headline is an interruption rather than an offer.
    const onScroll = () => setShown(window.scrollY > window.innerHeight * 0.6);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <button
      type="button"
      className={`wa-fab${shown ? ' is-in' : ''}`}
      aria-label="Chat with us on WhatsApp"
      onClick={() => openWhatsApp(WA_MESSAGES.support)}
    >
      <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true">
        <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.48s1.06 2.87 1.21 3.07c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.62.71.23 1.36.2 1.87.12.57-.09 1.75-.72 2-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35z"/>
        <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.96L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm0 18.13h-.01c-1.52 0-3.01-.41-4.31-1.18l-.31-.18-3.2.84.85-3.12-.2-.32a8.22 8.22 0 0 1-1.26-4.39c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.19 8.19 0 0 1 2.41 5.83c0 4.54-3.7 8.24-8.25 8.24z"/>
      </svg>
    </button>
  );
}
